"use client";

import { type EdgeBand, pimcoreFetcher } from "@tosize/ui";
import { ProductGroup, ProductionUnit, formatLocale } from "@tosize/ui";
import isEqual from "lodash.isequal";
import { type FC, type PropsWithChildren, useCallback, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import useSWR from "swr";
import type { FrontendQuery } from "../../../../apps/frontend/pimcore";
import { TYPE_STEP_IDENTIFIER } from "../components/Form/Steps/ProductionType";
import { SHAPE_DIMENSIONS_STEP_IDENTIFIER } from "../components/Form/Steps/ShapeDimensions";
import { edgeBandQuery } from "../graphql";
import useProducts from "../hooks/useProducts";
import type { Workpiece } from "../lib/workpiece";
import { useCardContext } from "./CardContext";
import { useConfig } from "./ConfigContext";
import {
	type PostData,
	PostDataContext,
	type PostDataContextValue,
	defaultData,
	defaultWorkpiece,
} from "./PostDataContext";

type Props = PropsWithChildren;

export const PostDataContextProvider: FC<Props> = ({ children }) => {
	const intl = useIntl();
	const { initialWorkpiece, initialPostData, productGroup, mode } = useConfig();
	const { products } = useProducts();
	const { onOpenCardsChange } = useCardContext();

	const [data, setData] = useState<PostData>(defaultData);
	const [workpiece, setWorkpiece] = useState<Workpiece>(defaultWorkpiece);
	const [initialLoad, setInitialLoad] = useState(false);

	const [selectedBand, setSelectedBand] = useState<EdgeBand | undefined>();

	const [initialSku, setInitialSku] = useState<string | undefined>();

	useSWR<Pick<FrontendQuery, "edgeBandQuery">, Error>(
		initialSku && !selectedBand
			? [
					edgeBandQuery,
					{ sku: initialSku, defaultLanguage: formatLocale(intl.locale, "language_COUNTRY") },
				]
			: null,
		{
			fetcher: pimcoreFetcher,
			onSuccess: (data) => setSelectedBand(data.edgeBandQuery),
		},
	);

	useEffect(() => {
		if (!initialLoad && products && initialPostData) {
			// set initial state & cards if product is in stock
			const selectedProduct = products.find((p) => p.stock && p.id === initialPostData.product);

			if (selectedProduct) {
				if (
					initialWorkpiece &&
					(Array.isArray(initialWorkpiece.finishes?.side) || !initialWorkpiece.finishes?.side)
				) {
					initialWorkpiece.finishes = defaultWorkpiece.finishes;
				}

				const sides = initialWorkpiece?.finishes?.side
					? Object.values(initialWorkpiece.finishes.side)
					: undefined;

				if (sides?.length && sides.every((value) => isEqual(value, sides[0]))) {
					const fields = sides[0] ? Object.values(sides[0])[0] : undefined;

					if (fields?.[0]?.option === "custom" && fields[0].option_parameters) {
						const sku =
							"sku" in fields[0].option_parameters ? fields[0].option_parameters.sku : undefined;

						setInitialSku(sku?.toString());
					}
				}

				const units = selectedProduct.available_production_units;
				const initialShapeDimensions = selectedProduct.checkModeEnabled
					? { width: 1000, length: 1000 }
					: undefined;

				setData((prev) => ({
					...prev,
					...initialPostData,
					sku: selectedProduct.sku,
					unit:
						initialPostData.unit ??
						units?.find((u) => u === ProductionUnit.CompletePanel) ??
						ProductionUnit.M2,
				}));

				setWorkpiece((prev) => ({
					...prev,
					...initialWorkpiece,
					shape_dimensions: {
						...prev.shape_dimensions,
						...initialShapeDimensions,
						...initialWorkpiece?.shape_dimensions,
					},
				}));

				onOpenCardsChange(
					productGroup === ProductGroup.Plastic_Sheet ||
						(initialPostData.unit && mode === "addInDialog")
						? [SHAPE_DIMENSIONS_STEP_IDENTIFIER]
						: initialPostData.unit
							? [TYPE_STEP_IDENTIFIER, SHAPE_DIMENSIONS_STEP_IDENTIFIER]
							: [TYPE_STEP_IDENTIFIER],
				);
			}
			setInitialLoad(true);
		}
	}, [
		initialLoad,
		initialPostData,
		initialWorkpiece,
		mode,
		productGroup,
		products,
		onOpenCardsChange,
	]);

	const hasFinishes = initialWorkpiece?.finishes?.side
		? Object.values(initialWorkpiece?.finishes?.side).length > 0
		: false;
	const [finishSwitchChecked, setFinishSwitchChecked] = useState(hasFinishes);

	const [canvasSettings, setCanvasSettings] =
		useState<PostDataContextValue["canvasSettings"]>(undefined);
	const onFinishSwitchCheckedChange = useCallback(
		(checked: boolean) => {
			setFinishSwitchChecked(checked);

			setWorkpiece((prev) => ({
				...prev,
				finishes: checked ? workpiece.finishes : { side: {} },
			}));
		},
		[workpiece.finishes],
	);

	return (
		<PostDataContext.Provider
			value={{
				defaultData,
				data,
				setData,

				defaultWorkpiece,
				workpiece,
				setWorkpiece,

				finishSwitchChecked,
				setFinishSwitchChecked,

				canvasSettings,
				setCanvasSettings,
				onFinishSwitchCheckedChange,

				selectedBand,
				setSelectedBand,
			}}
		>
			{children}
		</PostDataContext.Provider>
	);
};
